import Data from 'data'
import { ItemDefinition, PassiveSkillDefinition, TattooDefinition } from './data/types'

export type LibraryNode = {
  name: string
  filter: (item: any, ...args: any[]) => any
  expanded?: boolean
  children?: LibraryNode[]
  types?: string[]
  type?: 'skill' | 'passive' | 'tattoo' | 'gem'
  tree?: {
    atlas?: boolean
    ascendancy?: string
  }
}

const typeCategory = (name: string, ...types: string[]) => ({
  name,
  filter: (item: ItemDefinition) => item && types.includes(item.item_class),
  types
})

const armorCategory = (name: string, type: string): LibraryNode => ({
  ...typeCategory(name, type),
  children: [
    {
      name: 'Armour',
      filter: (item: ItemDefinition) =>
        item &&
        item.item_class === type &&
        item.properties &&
        item.properties.armour &&
        !item.properties.evasion &&
        !item.properties.energy_shield
    },
    {
      name: 'Evasion',
      filter: (item: ItemDefinition) =>
        item &&
        item.item_class === type &&
        item.properties &&
        !item.properties.armour &&
        item.properties.evasion &&
        !item.properties.energy_shield
    },
    {
      name: 'Energy Shield',
      filter: (item: ItemDefinition) =>
        item &&
        item.item_class === type &&
        item.properties &&
        !item.properties.armour &&
        !item.properties.evasion &&
        item.properties.energy_shield
    },
    {
      name: 'Armour and Evasion',
      filter: (item: ItemDefinition) =>
        item &&
        item.item_class === type &&
        item.properties &&
        item.properties.armour &&
        item.properties.evasion &&
        !item.properties.energy_shield
    },
    {
      name: 'Armour and Energy Shield',
      filter: (item: ItemDefinition) =>
        item &&
        item.item_class === type &&
        item.properties &&
        item.properties.armour &&
        !item.properties.evasion &&
        item.properties.energy_shield
    },
    {
      name: 'Evasion and Energy Shield',
      filter: (item: ItemDefinition) =>
        item &&
        item.item_class === type &&
        item.properties &&
        !item.properties.armour &&
        item.properties.evasion &&
        item.properties.energy_shield
    },
    {
      name: 'Armour, Evasion and Energy Shield',
      filter: (item: ItemDefinition) =>
        item &&
        item.item_class === type &&
        item.properties &&
        item.properties.armour &&
        item.properties.evasion &&
        item.properties.energy_shield
    }
  ]
})

const autoFilter = (node: Omit<LibraryNode, 'filter'> & { children: LibraryNode[] }): LibraryNode => ({
  ...node,
  filter: (item: any) => item && node.children.some((c) => c.filter(item))
})
const withOther = (otherName: string, node: LibraryNode & { children: LibraryNode[] }): LibraryNode => ({
  ...node,
  children: [
    ...node.children,
    {
      name: otherName,
      filter: (item: any) => item && node.filter(item) && !node.children.some((c) => c.filter(item))
    }
  ]
})

export const itemCategoryJewels = autoFilter({
  name: 'Jewels',
  children: [
    {
      name: 'Normal Jewels',
      filter: (item: ItemDefinition) => item && item.item_class === 'Jewel' && item.domain !== 'affliction_jewel',
      types: ['Jewel']
    },
    {
      name: 'Cluster Jewels',
      filter: (item: ItemDefinition) => item && item.item_class === 'Jewel' && item.domain === 'affliction_jewel',
      types: ['Jewel']
    },
    typeCategory('Abyss Jewels', 'AbyssJewel'),
    typeCategory('Charms', 'AnimalCharm')
  ]
})

export const itemCategoryGems = autoFilter({
  name: 'Skill Gems',
  children: [
    typeCategory('Active Skill Gems', 'Active Skill Gem'),
    typeCategory('Support Skill Gems', 'Support Skill Gem')
  ]
})

export const itemCategoryEquipment = autoFilter({
  name: 'Equipment',
  children: [
    typeCategory('Amulets', 'Amulet'),
    typeCategory('Belts', 'Belt'),
    armorCategory('Body Armours', 'Body Armour'),
    armorCategory('Boots', 'Boots'),
    armorCategory('Gloves', 'Gloves'),
    armorCategory('Helmets', 'Helmet'),
    typeCategory('Quivers', 'Quiver'),
    typeCategory('Rings', 'Ring'),
    armorCategory('Shields', 'Shield'),
    autoFilter({
      name: 'Weapons',
      children: [
        typeCategory('Bows', 'Bow'),
        typeCategory('Claws', 'Claw'),
        typeCategory('Daggers', 'Dagger'),
        typeCategory('Fishing Rods', 'FishingRod'),
        typeCategory('One Hand Axes', 'One Hand Axe'),
        typeCategory('One Hand Maces', 'One Hand Mace'),
        typeCategory('One Hand Swords', 'One Hand Sword'),
        typeCategory('Rune Daggers', 'Rune Dagger'),
        typeCategory('Sceptres', 'Sceptre'),
        typeCategory('Staves', 'Staff'),
        typeCategory('Thrusting One Hand Swords', 'Thrusting One Hand Sword'),
        typeCategory('Two Hand Axes', 'Two Hand Axe'),
        typeCategory('Two Hand Maces', 'Two Hand Mace'),
        typeCategory('Two Hand Swords', 'Two Hand Sword'),
        typeCategory('Wands', 'Wand'),
        typeCategory('Warstaves', 'Warstaff')
      ]
    })
  ]
})

export const itemCategoryFlasks = autoFilter({
  name: 'Flasks',
  children: [
    typeCategory('Life Flasks', 'LifeFlask'),
    typeCategory('Mana Flasks', 'ManaFlask'),
    typeCategory('Hybrid Flasks', 'HybridFlask'),
    typeCategory('Utility Flasks', 'UtilityFlask')
  ]
})

export const itemCategories: LibraryNode = {
  name: 'All Items',
  filter: () => true,
  children: [
    itemCategoryJewels,
    itemCategoryGems,
    itemCategoryEquipment,
    itemCategoryFlasks,
    typeCategory('Tinctures', 'Tincture'),
    typeCategory('Atlas Upgrade Items', 'AtlasUpgradeItem'),
    typeCategory('Memories', 'MemoryLine'),
    withOther('Other', {
      ...typeCategory('Currency', 'Currency', 'StackableCurrency'),
      children: [
        {
          name: 'Essences',
          filter: (item) => item && item.item_class === 'StackableCurrency' && item.tags.includes('essence')
        }
      ]
    }),
    typeCategory('Divination Cards', 'DivinationCard'),
    typeCategory('Expedition Logbooks', 'ExpeditionLogbook'),
    typeCategory('Incubators', 'IncubatorStackable'),
    typeCategory('Maps', 'Map'),
    typeCategory('Map Fragments', 'MapFragment')
  ]
}

export const createCategories = autoFilter({
  name: 'All Items',
  children: [
    itemCategoryJewels,
    itemCategoryEquipment,
    itemCategoryFlasks,
    typeCategory('Tinctures', 'Tincture'),
    typeCategory('Memories', 'MemoryLine'),
    typeCategory('Maps', 'Map')
  ]
})

export function makeExtendedCategories(): LibraryNode {
  return {
    name: 'All Items',
    filter: itemCategories.filter,
    children: [
      ...itemCategories.children!,
      {
        name: 'Active Skills',
        filter: () => true,
        type: 'skill'
      },
      {
        name: 'Passive Nodes',
        filter: () => null,
        children: [
          {
            name: 'Passive Skill Tree',
            filter: () => null,
            tree: {}
          },
          {
            name: 'Ascendancies',
            filter: () => null,
            children: Object.values(Data.characters)
              .flatMap((charData) => Object.entries(charData.ascendancies))
              .map(([ascendancy, { name }]) => ({
                name,
                filter: () => null,
                tree: { ascendancy }
              }))
          },
          {
            name: 'Atlas Passive Tree',
            filter: () => null,
            tree: { atlas: true }
          },
          {
            name: 'Special Passive Nodes',
            type: 'passive',
            filter: (node: PassiveSkillDefinition) => node && !node.used
          },
          {
            name: 'Tattoos',
            type: 'tattoo',
            filter: (tattoo: TattooDefinition) => tattoo && !tattoo.allocatedSkill
          }
        ]
      }
    ]
  }
}
